import { create } from 'zustand';
import { AnyCell, Cell, CellOutput, ContainerCell, Notebook, NotebookLanguage, isContainerCell } from '../types/notebook';
import {
  NotebookStep,
  notebookToLogicFunction,
  notebookToPipeline,
  notebookToDashboard,
} from '../types/steps';
import { getTenantId, getAccessToken } from './authStore';
import i18next from 'i18next';
import { placeBesideOrigin } from '../modules/files/workspaceStore';

const ONTOLOGY_API = import.meta.env.VITE_ONTOLOGY_SERVICE_URL || 'http://localhost:8004';
const KERNEL_API = import.meta.env.VITE_KERNEL_SERVICE_URL || 'http://localhost:8026';
const INFERENCE_API = import.meta.env.VITE_INFERENCE_SERVICE_URL || 'http://localhost:8003';
const LOGIC_API = import.meta.env.VITE_LOGIC_SERVICE_URL || 'http://localhost:8012';
const PIPELINE_API = import.meta.env.VITE_PIPELINE_SERVICE_URL || 'http://localhost:8002';

function uid(): string {
  return (globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2) + Date.now().toString(36));
}

function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const h: Record<string, string> = { 'x-tenant-id': getTenantId(), ...extra };
  const token = getAccessToken();
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

/** The notebook's kernel language (DS-6); anything unknown is Python. */
export function notebookLanguage(nb: Pick<Notebook, 'environment'> | undefined | null): NotebookLanguage {
  return nb?.environment?.language === 'r' ? 'r' : 'python';
}

/** The `language` an execute request carries for a cell. */
export function cellLanguage(kind: Cell['kind']): 'python' | 'sql' | 'r' {
  return kind === 'sql' ? 'sql' : kind === 'r' ? 'r' : 'python';
}

function toNotebook(raw: Record<string, unknown>): Notebook {
  const env = raw.environment as { packages?: unknown; language?: unknown } | undefined;
  return {
    id: String(raw.id ?? ''),
    name: String(raw.name ?? 'Untitled Notebook'),
    description: String(raw.description ?? ''),
    cells: Array.isArray(raw.cells) ? (raw.cells as AnyCell[]) : [],
    environment: {
      packages: Array.isArray(env?.packages) ? (env.packages as unknown[]).map(String) : [],
      language: env?.language === 'r' ? 'r' : 'python',
    },
    createdAt: String(raw.created_at ?? new Date().toISOString()),
    updatedAt: String(raw.updated_at ?? new Date().toISOString()),
  };
}

export interface MaterializeResult {
  ok: boolean;
  columns?: { name: string; dtype: string }[];
  rows?: Record<string, unknown>[];
  n_total?: number;
  truncated?: boolean;
  error?: string;
}

export interface ExportProperty {
  name: string;
  display_name: string;
  data_type: string;
  semantic_type: string;
  sample_values?: string[];
}

export interface ExportPreview {
  mode: 'new' | 'append';
  proposed?: { name: string; display_name: string; properties: ExportProperty[] };
  object_type?: { id: string; name: string; display_name: string; properties: ExportProperty[] };
  extra_columns?: string[];
  missing_columns?: string[];
  sample_rows: Record<string, unknown>[];
  n_total: number;
  key_field_suggestion: string;
}

// One entry in the live generation trace shown while the agent works — either
// a bit of its narration ("Looking for expense object types…") or a tool step
// (running Python, building a pipeline) with its status.
export interface TraceStep {
  id: string;
  kind: 'narration' | 'tool';
  text?: string;
  tool?: string;
  label?: string;
  code?: string;
  running?: boolean;
  ok?: boolean;
  summary?: string;
}

interface WorkbenchStore {
  notebooks: Notebook[];
  currentNotebookId: string | null;
  kernelSessionId: string | null;
  loading: boolean;
  generating: boolean;
  // Live trace of the agent's steps during generateFromPrompt (streaming).
  generationTrace: TraceStep[];

  fetchNotebooks: () => Promise<void>;
  createNotebook: (name?: string, language?: NotebookLanguage) => Promise<Notebook>;
  deleteNotebook: (id: string) => Promise<void>;
  openNotebook: (id: string) => Promise<void>;
  closeNotebook: () => Promise<void>;

  updateCell: (cellId: string, patch: Partial<Cell>) => void;
  updateContainer: (cellId: string, patch: Partial<Pick<ContainerCell, 'name' | 'steps'>>) => void;
  setCells: (cells: AnyCell[]) => void;
  appendCells: (cells: AnyCell[]) => void;
  removeCell: (cellId: string) => void;

  saveNotebook: () => Promise<void>;
  renameNotebook: (name: string) => Promise<void>;

  ensureKernel: () => Promise<string>;
  /** Set the notebook's libraries (DS-3), save, and restart the kernel with them. */
  setEnvironment: (packages: string[]) => Promise<void>;
  /** Switch the notebook's kernel language (DS-6): saves it with an empty
   *  package list (packages belong to one language's catalog) and restarts
   *  the kernel. */
  setLanguage: (language: NotebookLanguage) => Promise<void>;
  teardownKernel: () => Promise<void>;
  runCell: (cellId: string) => Promise<void>;
  generateFromPrompt: (prompt: string) => Promise<void>;

  // Notebook → Ontology export (isolated preview, then explicit commit).
  materialize: (expr: string, limit?: number) => Promise<MaterializeResult>;
  previewOntologyExport: (req: {
    rows: Record<string, unknown>[]; mode: 'new' | 'append';
    object_type_id?: string; suggested_name?: string;
  }) => Promise<ExportPreview>;
  commitOntologyExport: (req: {
    rows: Record<string, unknown>[]; mode: 'new' | 'append';
    object_type_id?: string; name?: string; properties?: ExportProperty[];
    key_field: string; notebook_id?: string;
  }) => Promise<{ object_type_id: string; created: boolean; ingested: number }>;

  // Structured-step authoring + export (the "be like Logic Studio" path).
  runStep: (cellId: string) => Promise<void>;
  exportToLogic: () => Promise<{ id: string; name: string }>;
  exportToPipeline: () => Promise<{ id: string; name: string; warnings: string[] }>;
  exportToDashboard: () => Promise<{ id: string; name: string }>;
}

export const useWorkbenchStore = create<WorkbenchStore>((set, get) => ({
  notebooks: [],
  currentNotebookId: null,
  kernelSessionId: null,
  loading: false,
  generating: false,
  generationTrace: [],

  fetchNotebooks: async () => {
    set({ loading: true });
    try {
      const resp = await fetch(`${ONTOLOGY_API}/notebooks`, { headers: authHeaders() });
      if (resp.ok) {
        const data = (await resp.json()) as Record<string, unknown>[];
        set({ notebooks: data.map(toNotebook) });
      }
    } finally {
      set({ loading: false });
    }
  },

  createNotebook: async (name = 'Untitled Notebook', language: NotebookLanguage = 'python') => {
    const resp = await fetch(`${ONTOLOGY_API}/notebooks`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        name, description: '', cells: [],
        ...(language === 'r' ? { environment: { packages: [], language } } : {}),
      }),
    });
    if (!resp.ok) throw new Error(`Failed to create notebook: ${resp.status}`);
    const created = toNotebook((await resp.json()) as Record<string, unknown>);
    set((s) => ({ notebooks: [created, ...s.notebooks] }));
    return created;
  },

  deleteNotebook: async (id: string) => {
    const resp = await fetch(`${ONTOLOGY_API}/notebooks/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (!resp.ok && resp.status !== 404) throw new Error(`Failed to delete notebook: ${resp.status}`);
    set((s) => ({
      notebooks: s.notebooks.filter((n) => n.id !== id),
      currentNotebookId: s.currentNotebookId === id ? null : s.currentNotebookId,
    }));
  },

  openNotebook: async (id: string) => {
    const resp = await fetch(`${ONTOLOGY_API}/notebooks/${id}`, { headers: authHeaders() });
    if (!resp.ok) throw new Error(`Failed to load notebook: ${resp.status}`);
    const nb = toNotebook((await resp.json()) as Record<string, unknown>);
    set((s) => ({
      notebooks: s.notebooks.some((n) => n.id === nb.id)
        ? s.notebooks.map((n) => (n.id === nb.id ? nb : n))
        : [nb, ...s.notebooks],
      currentNotebookId: nb.id,
    }));
  },

  closeNotebook: async () => {
    const { kernelSessionId } = get();
    if (kernelSessionId) {
      try {
        await fetch(`${KERNEL_API}/kernel/sessions/${kernelSessionId}`, {
          method: 'DELETE',
          headers: authHeaders(),
        });
      } catch {
        // ignore — kernel may already be gone
      }
    }
    set({ currentNotebookId: null, kernelSessionId: null });
  },

  updateCell: (cellId, patch) => {
    const id = get().currentNotebookId;
    if (!id) return;
    set((s) => ({
      notebooks: s.notebooks.map((n) =>
        n.id !== id ? n : {
          ...n,
          cells: n.cells.map((c) => (c.id === cellId && !isContainerCell(c) ? { ...c, ...patch } : c)),
        },
      ),
    }));
  },

  updateContainer: (cellId, patch) => {
    const id = get().currentNotebookId;
    if (!id) return;
    set((s) => ({
      notebooks: s.notebooks.map((n) =>
        n.id !== id ? n : {
          ...n,
          cells: n.cells.map((c) => (c.id === cellId && isContainerCell(c) ? { ...c, ...patch } : c)),
        },
      ),
    }));
  },

  setCells: (cells) => {
    const id = get().currentNotebookId;
    if (!id) return;
    set((s) => ({
      notebooks: s.notebooks.map((n) => (n.id === id ? { ...n, cells } : n)),
    }));
  },

  appendCells: (cells) => {
    const id = get().currentNotebookId;
    if (!id) return;
    set((s) => ({
      notebooks: s.notebooks.map((n) => (n.id === id ? { ...n, cells: [...n.cells, ...cells] } : n)),
    }));
  },

  removeCell: (cellId) => {
    const id = get().currentNotebookId;
    if (!id) return;
    set((s) => ({
      notebooks: s.notebooks.map((n) =>
        n.id !== id ? n : { ...n, cells: n.cells.filter((c) => c.id !== cellId) },
      ),
    }));
  },

  saveNotebook: async () => {
    const id = get().currentNotebookId;
    if (!id) return;
    const nb = get().notebooks.find((n) => n.id === id);
    if (!nb) return;
    await fetch(`${ONTOLOGY_API}/notebooks/${id}`, {
      method: 'PUT',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        name: nb.name, description: nb.description, cells: nb.cells,
        environment: { packages: nb.environment?.packages ?? [], language: notebookLanguage(nb) },
      }),
    });
  },

  renameNotebook: async (name) => {
    const id = get().currentNotebookId;
    if (!id) return;
    set((s) => ({
      notebooks: s.notebooks.map((n) => (n.id === id ? { ...n, name } : n)),
    }));
    await get().saveNotebook();
  },

  ensureKernel: async () => {
    const existing = get().kernelSessionId;
    if (existing) return existing;
    const nbId = get().currentNotebookId;
    const nb = get().notebooks.find((n) => n.id === nbId);
    const packages = nb?.environment?.packages ?? [];
    const language = notebookLanguage(nb);
    const resp = await fetch(`${KERNEL_API}/kernel/sessions`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      // Libraries come from the read-only envs volume; the kernel refuses
      // (409) libraries that are not installed yet instead of installing.
      // An R notebook (DS-6) asks for an IRkernel session.
      body: JSON.stringify({
        ...(packages.length ? { packages } : {}),
        ...(language === 'r' ? { language } : {}),
      }),
    });
    if (resp.status === 409 || resp.status === 400) {
      let detail = '';
      try { detail = ((await resp.json()) as { detail?: string }).detail || ''; } catch { /* not JSON */ }
      throw new Error(i18next.t('workbench.environment.notReady', 'Las librerías del cuaderno no están listas ({{detail}}). Ábrelas en «Librerías».', { detail }));
    }
    if (!resp.ok) throw new Error(`Failed to start kernel: ${resp.status}`);
    const data = (await resp.json()) as { session_id: string };
    set({ kernelSessionId: data.session_id });
    return data.session_id;
  },

  setEnvironment: async (packages) => {
    const id = get().currentNotebookId;
    if (!id) return;
    set((s) => ({
      notebooks: s.notebooks.map((n) => (n.id === id ? { ...n, environment: { packages, language: notebookLanguage(n) } } : n)),
    }));
    await get().saveNotebook();
    // A running kernel can't gain sys.path entries safely after its sandbox is
    // sealed: restart it so the next cell runs with the new libraries.
    await get().teardownKernel();
    await get().ensureKernel();
  },

  setLanguage: async (language) => {
    const id = get().currentNotebookId;
    if (!id) return;
    const nb = get().notebooks.find((n) => n.id === id);
    if (!nb || notebookLanguage(nb) === language) return;
    set((s) => ({
      notebooks: s.notebooks.map((n) => (n.id === id ? { ...n, environment: { packages: [], language } } : n)),
    }));
    await get().saveNotebook();
    // A kernel runs one language: replace it.
    await get().teardownKernel();
    await get().ensureKernel();
  },

  // Tear down only the kernel (used by the editor's unmount cleanup). Crucially
  // does NOT touch currentNotebookId — resetting that from an effect cleanup is
  // what made the editor close itself under React StrictMode's dev double-invoke.
  teardownKernel: async () => {
    const sid = get().kernelSessionId;
    if (sid) {
      try {
        await fetch(`${KERNEL_API}/kernel/sessions/${sid}`, { method: 'DELETE', headers: authHeaders() });
      } catch { /* kernel may already be gone */ }
    }
    set({ kernelSessionId: null });
  },

  runCell: async (cellId) => {
    const id = get().currentNotebookId;
    if (!id) return;
    const nb = get().notebooks.find((n) => n.id === id);
    const cell = nb?.cells.find((c) => c.id === cellId);
    if (!cell) return;
    if (isContainerCell(cell)) return; // containers aren't run as a whole — their steps are config-only
    if (cell.kind === 'markdown' || cell.kind === 'prompt') return;
    if (cell.kind === 'step') { await get().runStep(cellId); return; }

    get().updateCell(cellId, { running: true });
    try {
      const exec = async () => {
        const session = await get().ensureKernel();
        return fetch(`${KERNEL_API}/kernel/sessions/${session}/execute`, {
          method: 'POST',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          // SQL cells run as SQL (DuckDB in the kernel), not as Python; R
          // cells only in an R notebook's kernel (the service refuses a mix).
          body: JSON.stringify({ code: cell.source, timeout_sec: 60, language: cellLanguage(cell.kind) }),
        });
      };
      let resp = await exec();
      // The kernel may have been reaped (idle TTL) or torn down by a transient
      // editor remount — recreate the session once and retry rather than
      // surfacing an empty/confusing output.
      if (resp.status === 404) {
        set({ kernelSessionId: null });
        resp = await exec();
      }
      if (resp.status === 400) {
        // A cell the kernel refuses (e.g. a Python cell in an R notebook):
        // show the service's reason as the cell's error.
        let detail = '';
        try { detail = ((await resp.json()) as { detail?: string }).detail || ''; } catch { /* not JSON */ }
        get().updateCell(cellId, {
          running: false,
          output: {
            status: 'error', outputs: [],
            error: { ename: 'CellLanguageError', evalue: detail || `HTTP ${resp.status}`, traceback: [] },
            executed_at: new Date().toISOString(),
          },
        });
        return;
      }
      const data = (await resp.json()) as {
        status: 'ok' | 'error';
        outputs: { mime_type: string; data: unknown; stream?: string }[];
        error?: { ename: string; evalue: string; traceback: string[] };
      };
      get().updateCell(cellId, {
        running: false,
        output: {
          status: data.status,
          outputs: data.outputs ?? [],
          error: data.error,
          executed_at: new Date().toISOString(),
        },
      });
    } catch (e) {
      get().updateCell(cellId, {
        running: false,
        output: {
          status: 'error',
          outputs: [],
          error: { ename: 'NetworkError', evalue: String(e), traceback: [] },
          executed_at: new Date().toISOString(),
        },
      });
    }
    await get().saveNotebook();
  },

  generateFromPrompt: async (prompt: string) => {
    const id = get().currentNotebookId;
    if (!id) return;
    const nb = get().notebooks.find((n) => n.id === id);
    if (!nb) return;

    // Map one streamed backend cell into a frontend cell.
    const mapCell = (c: {
      id?: string; kind: string; name?: string; source?: string;
      block?: NotebookStep; steps?: NotebookStep[]; output?: CellOutput;
      schedule?: { name: string; cron_expression: string; enabled?: boolean };
    }): AnyCell => {
      if (c.kind === 'pipeline' || c.kind === 'logic-function') {
        return {
          id: c.id || uid(), kind: c.kind, name: c.name || '', steps: c.steps || [],
          ...(c.schedule ? { schedule: c.schedule } : {}),
        };
      }
      return {
        id: c.id || uid(),
        kind: (c.kind as Cell['kind']) || 'markdown',
        source: c.source || '',
        ...(c.block ? { block: c.block } : {}),
        ...(c.output ? { output: c.output } : {}),
      };
    };
    const pushTrace = (t: TraceStep) =>
      set((s) => ({ generationTrace: [...s.generationTrace, t] }));
    const finishLastTool = (ok: boolean, summary: string) =>
      set((s) => {
        const tr = [...s.generationTrace];
        for (let i = tr.length - 1; i >= 0; i--) {
          if (tr[i].kind === 'tool' && tr[i].running) { tr[i] = { ...tr[i], running: false, ok, summary }; break; }
        }
        return { generationTrace: tr };
      });

    set({ generating: true, generationTrace: [] });
    // Show the user's message immediately; capture prior cells for context.
    const priorCells = nb.cells;
    get().appendCells([{ id: uid(), kind: 'prompt', source: prompt }]);

    try {
      const kernelSessionId = get().kernelSessionId;
      const resp = await fetch(`${INFERENCE_API}/infer/workbench/stream`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json', Accept: 'text/event-stream' }),
        // The notebook's language (DS-9): in an R notebook the assistant
        // writes and runs R cells instead of Python.
        body: JSON.stringify({ prompt, cells: priorCells, kernel_session_id: kernelSessionId, language: notebookLanguage(nb) }),
      });
      if (!resp.ok || !resp.body) throw new Error(`Generation failed (${resp.status})`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop() || '';
        for (const frame of frames) {
          const line = frame.trim();
          if (!line.startsWith('data: ')) continue;
          let ev: Record<string, unknown>;
          try { ev = JSON.parse(line.slice(6)); } catch { continue; }
          const type = ev.type as string;
          if (type === 'narration') {
            pushTrace({ id: uid(), kind: 'narration', text: String(ev.text || '') });
          } else if (type === 'step') {
            pushTrace({ id: uid(), kind: 'tool', tool: String(ev.tool || ''), label: String(ev.label || ev.tool || ''), code: ev.code ? String(ev.code) : undefined, running: true });
          } else if (type === 'step_result') {
            finishLastTool(!!ev.ok, String(ev.summary || ''));
          } else if (type === 'cell' || type === 'cell_update') {
            // The backend re-emits container cells (same id) as their steps
            // are filled in — upsert by id so updates replace instead of
            // duplicating, and late steps aren't lost.
            const mapped = mapCell(ev.cell as Parameters<typeof mapCell>[0]);
            const nbId = get().currentNotebookId;
            const current = get().notebooks.find((n) => n.id === nbId);
            if (current?.cells.some((c) => c.id === mapped.id)) {
              set((s) => ({
                notebooks: s.notebooks.map((n) => (n.id === nbId
                  ? { ...n, cells: n.cells.map((c) => (c.id === mapped.id ? mapped : c)) }
                  : n)),
              }));
            } else {
              get().appendCells([mapped]);
            }
          } else if (type === 'error') {
            pushTrace({ id: uid(), kind: 'narration', text: `Error: ${String(ev.error || 'generation failed')}` });
          }
        }
      }
      await get().saveNotebook();
    } catch (e) {
      pushTrace({ id: uid(), kind: 'narration', text: `Error: ${(e as Error).message}` });
    } finally {
      set({ generating: false, generationTrace: [] });
    }
  },

  materialize: async (expr, limit = 5000) => {
    const session = await get().ensureKernel();
    const resp = await fetch(`${KERNEL_API}/kernel/sessions/${session}/materialize`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ expr, limit }),
    });
    if (!resp.ok) throw new Error(`Could not read the variable (${resp.status})`);
    return (await resp.json()) as MaterializeResult;
  },

  previewOntologyExport: async (req) => {
    const resp = await fetch(`${ONTOLOGY_API}/notebooks/export/ontology/preview`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(req),
    });
    if (!resp.ok) {
      let msg = `Preview failed (${resp.status})`;
      try { msg = (await resp.json()).detail || msg; } catch { /* ignore */ }
      throw new Error(msg);
    }
    return (await resp.json()) as ExportPreview;
  },

  commitOntologyExport: async (req) => {
    const resp = await fetch(`${ONTOLOGY_API}/notebooks/export/ontology/commit`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(req),
    });
    if (!resp.ok) {
      let msg = `Save failed (${resp.status})`;
      try { msg = (await resp.json()).detail || msg; } catch { /* ignore */ }
      throw new Error(msg);
    }
    const out = (await resp.json()) as { object_type_id: string; created: boolean; ingested: number };
    // A type created here must land in the notebook's project, like one made in the
    // Ontology UI, or project-scoped selectors (Logic, Apps) never list it (VIDEO-22).
    if (out.created && out.object_type_id) {
      await placeBesideOrigin('object_type', out.object_type_id, 'notebook', req.notebook_id ?? get().currentNotebookId);
    }
    return out;
  },

  // Run a step cell through the logic runner. Sends every step block up to and
  // including this one (the runner resolves {stepId.result} refs between them),
  // and renders the target step's result like any other cell output.
  runStep: async (cellId) => {
    const id = get().currentNotebookId;
    if (!id) return;
    const nb = get().notebooks.find((n) => n.id === id);
    if (!nb) return;
    const target = nb.cells.find((c) => c.id === cellId);
    if (!target || target.kind !== 'step') return;

    // Collect step blocks in document order up to and including the target.
    const blocks: NotebookStep[] = [];
    let targetBlockId = '';
    for (const c of nb.cells) {
      if (c.kind !== 'step') continue;
      const block = stepBlock(c);
      if (!block) continue;
      blocks.push(block);
      if (c.id === cellId) { targetBlockId = block.id; break; }
    }
    if (!targetBlockId) {
      get().updateCell(cellId, {
        running: false,
        output: { status: 'error', outputs: [],
          error: { ename: 'StepError', evalue: 'This step has no valid block JSON.', traceback: [] },
          executed_at: new Date().toISOString() },
      });
      return;
    }

    get().updateCell(cellId, { running: true });
    try {
      const resp = await fetch(`${LOGIC_API}/logic/steps/preview`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ blocks, target_id: targetBlockId }),
      });
      const data = (await resp.json()) as { output?: unknown; trace?: unknown; error?: string };
      if (!resp.ok || data.error) {
        get().updateCell(cellId, {
          running: false,
          output: { status: 'error', outputs: [],
            error: { ename: 'StepError', evalue: data.error || `Preview failed (${resp.status})`, traceback: [] },
            executed_at: new Date().toISOString() },
        });
      } else {
        get().updateCell(cellId, {
          running: false,
          output: { status: 'ok',
            outputs: [{ mime_type: 'application/json', data: data.output ?? null }],
            executed_at: new Date().toISOString() },
        });
      }
    } catch (e) {
      get().updateCell(cellId, {
        running: false,
        output: { status: 'error', outputs: [],
          error: { ename: 'NetworkError', evalue: String(e), traceback: [] },
          executed_at: new Date().toISOString() },
      });
    }
    await get().saveNotebook();
  },

  exportToLogic: async () => {
    const nb = getCurrentNotebook();
    if (!nb) throw new Error('No notebook open');
    // Legacy whole-notebook export — scans flat (non-container) cells only.
    // Container blocks (pipeline/logic-function) export via their own local
    // Export button, see blockTypes.ts's `exportable.submit`.
    const cells = nb.cells.filter((c): c is Cell => !isContainerCell(c)).map(toSerializable);
    const draft = notebookToLogicFunction(cells, nb.name || 'Notebook flow', nb.description || '');
    if (!draft.blocks.length) throw new Error('Nothing to export — add step or python cells first.');
    const resp = await fetch(`${LOGIC_API}/logic/functions`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(draft),
    });
    if (!resp.ok) {
      let msg = `Export failed (${resp.status})`;
      try { msg = (await resp.json()).detail || msg; } catch { /* ignore */ }
      throw new Error(msg);
    }
    const fn = (await resp.json()) as { id: string; name: string };
    return fn;
  },

  exportToPipeline: async () => {
    const nb = getCurrentNotebook();
    if (!nb) throw new Error('No notebook open');
    const cells = nb.cells.filter((c): c is Cell => !isContainerCell(c)).map(toSerializable);
    const { pipeline, warnings, mappedCount } = notebookToPipeline(cells, nb.name || 'Notebook pipeline', nb.description || '');
    if (!mappedCount) throw new Error(warnings[0] || 'No steps could be mapped to a pipeline.');
    const resp = await fetch(`${PIPELINE_API}/pipelines`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ ...pipeline, tenant_id: getTenantId() }),
    });
    if (!resp.ok) {
      let msg = `Export failed (${resp.status})`;
      try { msg = (await resp.json()).detail || msg; } catch { /* ignore */ }
      throw new Error(msg);
    }
    const p = (await resp.json()) as { id: string; name: string };
    return { ...p, warnings };
  },

  exportToDashboard: async () => {
    const nb = getCurrentNotebook();
    if (!nb) throw new Error('No notebook open');
    const cells = nb.cells.filter((c): c is Cell => !isContainerCell(c)).map(toSerializable);
    const draft = notebookToDashboard(cells, nb.name || 'Notebook dashboard');
    if (!draft) throw new Error('No object type referenced — add an ontology_query or ontology_update step first.');
    const resp = await fetch(`${ONTOLOGY_API}/apps`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(draft),
    });
    if (!resp.ok) {
      let msg = `Export failed (${resp.status})`;
      try { msg = (await resp.json()).detail || msg; } catch { /* ignore */ }
      throw new Error(msg);
    }
    const d = (await resp.json()) as { id: string; name: string };
    return d;
  },
}));

/** A step cell's block — parsed from its (editable) JSON source, falling back to
 *  the block field carried from generation. */
function stepBlock(cell: Cell): NotebookStep | null {
  if (cell.source && cell.source.trim()) {
    try {
      const parsed = JSON.parse(cell.source) as NotebookStep;
      if (parsed && typeof parsed === 'object' && parsed.type) return parsed;
    } catch { /* fall through to the carried block */ }
  }
  return cell.block ?? null;
}

/** Notebook Cell → the minimal shape the pure serializers consume. */
function toSerializable(cell: Cell) {
  return {
    id: cell.id,
    kind: cell.kind,
    source: cell.source,
    block: cell.kind === 'step' ? stepBlock(cell) ?? undefined : undefined,
  };
}

export const getCurrentNotebook = (): Notebook | undefined => {
  const s = useWorkbenchStore.getState();
  return s.notebooks.find((n) => n.id === s.currentNotebookId);
};
